(function (root) {
  'use strict';

  function safeUrl(value, image) {
    if (typeof value !== 'string' || !value.trim()) return '';
    const url = value.trim();
    if (!image && /^\/(?!\/)/.test(url) && !/[\\\s]/.test(url)) return url;
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'https:' && !parsed.username && !parsed.password ? parsed.href : '';
    } catch (_) { return ''; }
  }

  function destination(setting) {
    const url = setting.url || {};
    const type = url.type && url.type.id;
    if (type === 'link') return safeUrl(url.link);
    if (type === 'page' && url.page && url.page.handle) return '/pages/' + encodeURIComponent(url.page.handle);
    if ((!type || type === 'collection') && url.collection && url.collection.handle) {
      return '/collections/' + encodeURIComponent(url.collection.handle);
    }
    return '';
  }

  // Champ's shared Master carousel/picker settings retain their original NFL keys.
  function champPagePlacement(league, placement) {
    return (league === 'ncaa' ? 'nfl' : league) + '-' + placement;
  }

  function schoolLogoSource(team, fallback) {
    const image = safeUrl(team && team.image, true);
    if (image && team.champId && new URL(image).pathname.endsWith('/public/champ-logo/' + encodeURIComponent(team.champId) + '.svg')) return image;
    return fallback;
  }

  function findChampSchool(catalog, title, href) {
    const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').trim();
    const handle = destinationKey(href).split('/').pop().replace(/^college-/, '').replace(/-socks$/, '');
    const keys = new Set([normalize(title), normalize(handle)].filter(Boolean));
    const matches = (catalog || []).filter(school => [school.name, ...(school.aliases || [])].some(alias => keys.has(normalize(alias))));
    if (matches.length !== 1 || !/^[A-Za-z0-9_-]+$/.test(matches[0].id)) return null;
    const school = matches[0];
    return { champId: school.id, name: String(school.name || ''), aliases: school.aliases || [], image: school.hasLogo === false ? '' : 'https://us-central1-a2-migration.cloudfunctions.net/api/public/champ-logo/' + school.id + '.svg' };
  }

  function champCatalogTeams(catalog, destinations, fallbackHref) {
    const candidates = (destinations || []).filter(function (team) {
      return team && safeUrl(team.href) && !team.shopAll;
    });
    const seen = new Set();
    return (catalog || []).flatMap(function (school) {
      if (!school || !/^[A-Za-z0-9_-]+$/.test(school.id) || !String(school.name || '').trim() || seen.has(school.id)) return [];
      seen.add(school.id);
      const direct = candidates.find(function (team) { return team.champId === school.id; });
      const matches = direct ? [direct] : candidates.filter(function (team) {
        return Boolean(findChampSchool([school], team.fullTitle || team.title, team.href));
      });
      const linked = matches.length === 1 ? matches[0] : null;
      const search = (safeUrl(fallbackHref) || '/search') + '?type=product&q=' + encodeURIComponent(school.name);
      return [{
        id: school.id,
        champId: school.id,
        title: String(school.name),
        fullTitle: String(school.name),
        href: linked ? linked.href : search,
        image: school.hasLogo === false ? '' : 'https://us-central1-a2-migration.cloudfunctions.net/api/public/champ-logo/' + school.id + '.svg'
      }];
    }).sort(function (left, right) { return left.fullTitle.localeCompare(right.fullTitle); });
  }

  function selectTeams(data, options) {
    options = options || {};
    const menuTitle = String(options.menuTitle || 'NFL').toLowerCase();
    const logoUsage = String(options.logoUsage || 'nfl');
    const allowLegacy = options.allowLegacy === true;
    if (!data || !Array.isArray(data.megamenu)) return [];
    const roots = data.megamenu.filter(function (item) { return /^(Mobile - )?Fan Shop$/i.test(item.setting && item.setting.title); });
    roots.sort(function (a, b) { return Number(!!b.setting.hidden_desktop) - Number(!!a.setting.hidden_desktop); });
    for (const parent of roots) {
      const league = (parent.menus || []).find(function (item) {
        return item.setting && String(item.setting.title || '').toLowerCase() === menuTitle;
      });
      if (!league) continue;
      const teams = [];
      const seen = new Set();
      function walk(items) {
        (items || []).forEach(function (item) {
          const setting = item.setting || {};
          const href = destination(setting);
          const champId = setting.artomator_champ_profile_id;
          const legacyId = setting.url && setting.url.collection && setting.url.collection.handle || href;
          const id = champId || (allowLegacy ? legacyId : '');
          const selectedImage = options.logoFallback && (champId || setting.item_layout === 'image' || setting.image)
            ? schoolLogoSource({ champId: champId, image: setting.image }, options.logoFallback)
            : safeUrl(setting.image, true);
          if (id && href && selectedImage && !seen.has(id)) {
            seen.add(id);
            let image = selectedImage;
            if (image && champId) {
              const logoUrl = new URL(image);
              if (logoUrl.pathname.endsWith('/public/champ-logo/' + encodeURIComponent(champId) + '.svg')) {
                logoUrl.searchParams.delete('logo');
                logoUrl.searchParams.delete('v');
                logoUrl.searchParams.delete('item');
                logoUrl.searchParams.set('usage', champPagePlacement(logoUsage, 'rail'));
                image = logoUrl.href;
              }
            }
            teams.push({ id: id, champId: champId || '', title: String(setting.title || ''), fullTitle: String(setting.url && setting.url.collection && setting.url.collection.title || setting.title || ''), href: href, image: image });
          }
          walk(item.menus);
        });
      }
      walk(league.menus);
      if (teams.length) return teams;
    }
    return [];
  }

  function selectFeatures(payload) {
    if (!payload || payload.published !== true || !Array.isArray(payload.items)) return null;
    return payload.items.filter(function (item) {
      return item && item.enabled !== false && item.title && safeUrl(item.image, true) && safeUrl(item.url);
    }).slice(0, 20);
  }

  function selectHero(payload, fallbackAlt) {
    if (!payload || payload.published !== true || !payload.hero) return null;
    const image = safeUrl(payload.hero.image, true);
    if (!image) return null;
    return { image: image, alt: String(payload.hero.alt || fallbackAlt || 'NFL fan shop').slice(0, 240) };
  }

  function selectTopProducts(payload) {
    const config = payload && payload.published === true && payload.topProducts;
    if (!config || config.enabled === false || !config.tabs || !config.lists) return null;
    const keys = ['all', 'socks', 'boxers'];
    const tabs = keys.flatMap(function (key) {
      const tab = config.tabs[key];
      if (!tab || tab.enabled === false) return [];
      return [{ key: key, label: String(tab.label || key).trim().slice(0, 40) }];
    });
    if (!tabs.length) return null;
    const lists = {};
    keys.forEach(function (key) {
      lists[key] = (Array.isArray(config.lists[key]) ? config.lists[key] : []).flatMap(function (product, index) {
        const url = safeUrl(product && product.url);
        const image = safeUrl(product && product.image && product.image.url, true);
        const title = String(product && product.title || '').trim();
        if (!url || !image || !title) return [];
        const amount = Number(product.price && product.price.amount);
        return [{
          id: String(product.id || product.handle || key + '-' + index),
          productId: String(product.id || '').split('/').pop(),
          handle: String(product.handle || '').trim(),
          title: title,
          url: url,
          image: image,
          alt: String(product.image.alt || title).slice(0, 240),
          rank: Number(product.rank) || index + 1,
          productType: String(product.productType || '').trim(),
          price: Number.isFinite(amount) ? { amount: amount, currencyCode: String(product.price.currencyCode || 'USD') } : null
        }];
      }).slice(0, 10);
    });
    return {
      heading: String(config.heading || "This Week's Top 10").trim().slice(0, 100),
      tabs: tabs,
      lists: lists,
      generatedAt: config.generatedAt || null
    };
  }

  function featureDestination(item, options) {
    options = options || {};
    const collection = '/collections/' + encodeURIComponent(options.collectionHandle || 'nfl');
    const league = String(options.league || 'nfl').toLowerCase();
    const title = String(item && item.title || '').trim().toLowerCase();
    if (/\b(?:underwear|boxer briefs?)\b/.test(title)) return collection + '?cs_format=underwear';
    if (/\bcustom\w*\b/.test(title)) return collection + '?cs_design=customize';
    if (/\bcollabs?\b/.test(title)) return collection + '?cs_design=collabs';
    if (league === 'nfl' && /\bnfl players?\b/.test(title)) return collection + '?cs_design=nfl-players';
    return safeUrl(item && item.url);
  }

  function resolveTeams(menu, bundledMenu, options) {
    const teams = selectTeams(menu, options);
    const bundledTeams = selectTeams(bundledMenu, options);
    if (!teams.length) return bundledTeams;
    if (!options || !options.logoFallback) return teams;
    const linkedTeams = champTeamsByDestination(bundledTeams.filter(function (team) {
      return schoolLogoSource(team, '');
    }));
    return teams.map(function (team) {
      if (schoolLogoSource(team, '')) return team;
      const linked = linkedTeams.get(destinationKey(team.href));
      // Retain explicit profile choices; only repair missing or stale artwork
      // for the same school. Published labels and destinations remain intact.
      if (!linked || (team.champId && team.champId !== linked.champId)) return team;
      return { ...team, id: linked.id, champId: linked.champId, image: linked.image };
    });
  }

  function limitTeams(teams, limit) {
    const count = Number.parseInt(limit, 10);
    return Number.isFinite(count) && count > 0 ? teams.slice(0, count) : teams.slice();
  }

  function destinationKey(value) {
    try {
      return new URL(value, 'https://shop.invalid').pathname.replace(/\/+$/, '').toLowerCase();
    } catch (_) { return ''; }
  }

  function champTeamsByDestination(teams) {
    const result = new Map();
    (teams || []).forEach(function (team) {
      const key = destinationKey(team && team.href);
      if (key && team.champId && team.image) result.set(key, team);
    });
    return result;
  }

  function getLogoBounds(pixels, width, height, ignoreWhite) {
    function isWhite(index) {
      return pixels[index] >= 250 && pixels[index + 1] >= 250 && pixels[index + 2] >= 250;
    }
    // Opaque white canvases are padding; white artwork on transparency is not.
    const corners = [0, (width - 1) * 4, (height - 1) * width * 4, (width * height - 1) * 4];
    const whiteCanvas = corners.every(function (index) { return pixels[index + 3] >= 240 && isWhite(index); });
    let left = width;
    let top = height;
    let right = -1;
    let bottom = -1;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const index = (y * width + x) * 4;
        if (pixels[index + 3] < 16 || ((ignoreWhite || whiteCanvas) && isWhite(index))) continue;
        left = Math.min(left, x);
        top = Math.min(top, y);
        right = Math.max(right, x);
        bottom = Math.max(bottom, y);
      }
    }
    return right < left ? null : { x: left, y: top, width: right - left + 1, height: bottom - top + 1, sourceWidth: width, sourceHeight: height };
  }

  function getLogoOffset(bounds) {
    if (!bounds || !bounds.sourceWidth || !bounds.sourceHeight) return null;
    // Match object-fit: contain inside the square image, without resizing artwork.
    const scale = 100 / Math.max(bounds.sourceWidth, bounds.sourceHeight);
    return {
      x: (bounds.sourceWidth / 2 - bounds.x - bounds.width / 2) * scale,
      y: (bounds.sourceHeight / 2 - bounds.y - bounds.height / 2) * scale,
    };
  }

  function filterSchoolTiles(tiles, query) {
    function normalize(value) {
      return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').trim();
    }
    const terms = normalize(query).split(/\s+/).filter(Boolean);
    let count = 0;
    Array.from(tiles).forEach(function (tile) {
      const shopAll = tile.hasAttribute('data-shop-all') || tile.hasAttribute('data-nfl-shop-all');
      const name = normalize(tile.textContent);
      const matches = terms.every(function (term) { return name.includes(term); });
      tile.hidden = shopAll ? terms.length > 0 : !matches;
      if (!shopAll && !tile.hidden) count++;
    });
    return count;
  }

  function getLogoPlacement(bounds) {
    const offset = getLogoOffset(bounds);
    if (!offset) return null;
    const sourceSize = Math.max(bounds.sourceWidth, bounds.sourceHeight);
    const artworkSize = Math.max(bounds.width, bounds.height);
    if (!sourceSize || !artworkSize) return null;
    const scale = sourceSize / artworkSize;
    return { x: offset.x * scale, y: offset.y * scale, scale: scale };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { selectTeams: selectTeams, resolveTeams: resolveTeams, limitTeams: limitTeams, destinationKey: destinationKey, champTeamsByDestination: champTeamsByDestination, selectFeatures: selectFeatures, selectHero: selectHero, selectTopProducts: selectTopProducts, featureDestination: featureDestination, safeUrl: safeUrl, getLogoBounds: getLogoBounds, getLogoOffset: getLogoOffset, getLogoPlacement: getLogoPlacement };
  if (typeof module !== 'undefined' && module.exports) module.exports.filterSchoolTiles = filterSchoolTiles;
  if (typeof module !== 'undefined' && module.exports) module.exports.schoolLogoSource = schoolLogoSource;
  if (typeof module !== 'undefined' && module.exports) module.exports.champPagePlacement = champPagePlacement;
  if (typeof module !== 'undefined' && module.exports) module.exports.findChampSchool = findChampSchool;
  if (typeof module !== 'undefined' && module.exports) module.exports.champCatalogTeams = champCatalogTeams;
  if (!root.document) return;

  function node(tag, className, text) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text) el.textContent = text;
    return el;
  }

  function init(hub) {
    if (hub.dataset.initialized) return;
    hub.dataset.initialized = 'true';
    const league = String(hub.dataset.league || 'nfl').toLowerCase();
    const collectionHandle = hub.dataset.collectionHandle || league;
    const instantFallback = hub.dataset.instantFallback === 'true';
    const schoolLogoFallback = hub.dataset.schoolLogoFallback
      ? new URL(hub.dataset.schoolLogoFallback, document.baseURI).href : '';
    const menuOptions = {
      menuTitle: hub.dataset.menuTitle || (league === 'ncaa' ? 'College' : 'NFL'),
      logoUsage: hub.dataset.logoUsage || league,
      allowLegacy: hub.dataset.allowLegacyTeams === 'true',
      logoFallback: schoolLogoFallback
    };
    const teamRail = hub.querySelector('[data-team-rail]');
    const shopAllTile = teamRail.querySelector('[data-shop-all], [data-nfl-shop-all]');
    const featuredRail = hub.querySelector('[data-featured-rail]');
    const featured = hub.querySelector('[data-featured]');
    const topProducts = hub.querySelector('[data-top-products]');
    const topProductsRail = hub.querySelector('[data-top-products-rail]');
    const hero = hub.previousElementSibling && hub.previousElementSibling.matches('[data-league-hero], [data-nfl-hero]') ? hub.previousElementSibling : null;
    const heroImage = hero && hero.querySelector('img');
    const toggle = hub.querySelector('[data-teams-toggle]');
    const dialog = hub.querySelector('[data-team-dialog]');
    const teamGrid = hub.querySelector('[data-team-grid]');
    const teamScroll = hub.querySelector('[data-team-scroll]');
    const abort = new AbortController();
    const cleanups = [];
    function showFallbackHero() {
      if (hero) {
        hero.removeAttribute('data-nfl-hero-pending');
        hero.removeAttribute('data-league-hero-pending');
      }
    }
    function showPublishedHero(publishedHero) {
      if (!publishedHero || !heroImage) {
        showFallbackHero();
        return;
      }
      const nextImage = new Image();
      nextImage.onload = function () {
        if (!hub.isConnected) return;
        heroImage.removeAttribute('srcset');
        heroImage.removeAttribute('sizes');
        heroImage.src = publishedHero.image;
        heroImage.alt = publishedHero.alt;
        if (hero.classList) hero.classList.remove('nfl-hub-hero--league-logo');
        showFallbackHero();
      };
      nextImage.onerror = showFallbackHero;
      nextImage.src = publishedHero.image;
    }
    let menu;
    try {
      menu = JSON.parse(hub.querySelector('[data-league-menu], [data-nfl-menu]').textContent);
      if (typeof menu === 'string') menu = JSON.parse(menu);
    } catch (_) { menu = null; }
    const teams = resolveTeams(menu, root.ArtomatorMenuData, menuOptions);
    const railTeams = limitTeams(teams, hub.dataset.railLimit);
    const serverTeamTiles = Array.from(teamRail.children, function (li) { return li.cloneNode(true); });
    const serverDestinations = serverTeamTiles.flatMap(function (li) {
      const link = li.querySelector('.nfl-hub__team');
      return link ? [{
        fullTitle: link.textContent.trim(),
        href: link.href,
        shopAll: li.hasAttribute('data-shop-all') || li.hasAttribute('data-nfl-shop-all')
      }] : [];
    });
    function teamTile(team) {
      const li = node('li');
      const link = node('a', 'nfl-hub__team');
      link.href = team.href;
      const logo = node('span', 'nfl-hub__logo');
      if (team.image) {
        const img = node('img');
        if (team.champId) img.crossOrigin = 'anonymous';
        img.src = team.image;
        img.alt = '';
        img.width = img.height = 64;
        img.loading = 'lazy';
        if (team.champId) img.dataset.logoSource = 'champ';
        logo.append(img);
      }
      link.append(logo, node('span', '', team.fullTitle));
      li.append(link);
      return li;
    }
    if (teams.length) {
      teamRail.replaceChildren();
      railTeams.forEach(function (team) {
        teamRail.append(teamTile(team));
      });
      if (shopAllTile) teamRail.append(shopAllTile);
    }
    function splitTeamLabels(scope) {
      scope.querySelectorAll('.nfl-hub__team > span:last-child').forEach(function (label) {
        const words = label.textContent.trim().split(/\s+/);
        if (words.length < 2) return;
        const nickname = words.pop();
        label.classList.add('nfl-hub__team-name');
        label.replaceChildren(
          node('span', 'nfl-hub__team-name-line', words.join(' ')),
          document.createTextNode(' '),
          node('span', 'nfl-hub__team-name-line', nickname)
        );
      });
    }
    splitTeamLabels(teamRail);
    function resetTeamRail() {
      teamRail.scrollLeft = 0;
    }
    resetTeamRail();
    root.requestAnimationFrame(resetTeamRail);
    root.addEventListener('pageshow', resetTeamRail, { signal: abort.signal });
    toggle.hidden = !teamRail.children.length;
    const catalogTeams = schoolLogoFallback && root.ChampSchoolCatalog
      ? champCatalogTeams(root.ChampSchoolCatalog, teams.concat(serverDestinations), '/search')
      : [];
    const dialogTiles = catalogTeams.length
      ? catalogTeams.map(teamTile)
      : hub.dataset.dialogAllTeams === 'true' && serverTeamTiles.length
        ? serverTeamTiles.filter(function (li) { return !li.hasAttribute('data-shop-all'); })
        : Array.from(teamRail.children, function (li) {
            return li.hasAttribute('data-shop-all') ? null : li.cloneNode(true);
          }).filter(Boolean);
    teamGrid.replaceChildren(...dialogTiles);
    splitTeamLabels(teamGrid);
    const schoolSearch = hub.querySelector('[data-school-search]');
    const schoolSearchStatus = hub.querySelector('[data-school-search-status]');
    function updateSchoolSearch() {
      if (!schoolSearch) return;
      const count = filterSchoolTiles(teamGrid.children, schoolSearch.value);
      schoolSearchStatus.textContent = count
        ? count + (count === 1 ? ' school found' : ' schools found')
        : 'No schools found. Try another school or mascot name.';
      teamScroll.scrollTop = 0;
    }
    if (schoolSearch) {
      schoolSearch.addEventListener('input', updateSchoolSearch, { signal: abort.signal });
      updateSchoolSearch();
    }

    // Navigation supplies names and destinations only. NCAA artwork must come
    // from a linked Champ logo or the Rock 'Em placeholder.
    const champTeams = champTeamsByDestination(teams);
    function applySchoolCatalog(catalog) {
      if (schoolLogoFallback && hub.dataset.dialogAllTeams === 'true') {
        const directoryTeams = champCatalogTeams(catalog, teams.concat(serverDestinations), '/search');
        if (directoryTeams.length) {
          const nextTiles = directoryTeams.map(teamTile);
          teamGrid.replaceChildren(...nextTiles);
          splitTeamLabels(teamGrid);
          updateSchoolSearch();
        }
      }
      const scopes = schoolLogoFallback ? [teamRail, teamGrid] : [teamGrid];
      scopes.forEach(function (scope) {
        scope.querySelectorAll('.nfl-hub__team').forEach(function (link) {
          const catalogTeam = schoolLogoFallback && findChampSchool(catalog, link.textContent, link.href);
          const team = catalogTeam || champTeams.get(destinationKey(link.href));
          if (!team && !schoolLogoFallback) return;
          if (link.closest('[data-shop-all], [data-nfl-shop-all]')) return;
          const logo = link.querySelector('.nfl-hub__logo');
          if (!logo) return;
          let img = logo.querySelector('img');
          if (!img) {
            img = node('img');
            img.alt = '';
            img.width = img.height = 64;
            img.loading = 'lazy';
            logo.append(img);
          }
          let source = schoolLogoFallback ? schoolLogoSource(team, schoolLogoFallback) : team.image;
          if (root.ChampWebsiteLogos) source = root.ChampWebsiteLogos.logoUrl(source, champPagePlacement(menuOptions.logoUsage, scope === teamGrid ? 'picker' : 'rail'));
          img.dataset.logoSource = source === schoolLogoFallback ? 'placeholder' : 'champ';
          if (img.dataset.logoSource === 'champ') {
            img.crossOrigin = 'anonymous';
            img.fetchPriority = scope === teamRail ? 'high' : 'low';
            img.dataset.champLogoUrl = source;
            delete img.dataset.champLogoRetries;
          }
          if (img.src !== source) {
            delete img.dataset.logoReady;
            delete img.dataset.logoCentered;
            ['--nfl-logo-offset-x', '--nfl-logo-offset-y', '--nfl-logo-scale'].forEach(key => img.style.removeProperty(key));
            img.src = source;
          }
        });
      });
      hub.querySelectorAll('.nfl-hub__logo img').forEach(prepareLogo);
    }

    const logoMeasurements = new Map();
    function markLogoReady(img) {
      img.dataset.logoReady = 'true';
    }
    function centerLogo(img) {
      const source = img.currentSrc || img.src;
      // Lazy images may report `complete` with no dimensions before their
      // request starts. Wait for load/error instead of treating that state as
      // a broken Champ logo.
      if (!source || !img.naturalWidth) return;
      const ignoreWhite = root.getComputedStyle(img).mixBlendMode === 'multiply';
      const key = source + '|' + ignoreWhite;
      if (!logoMeasurements.has(key)) {
        logoMeasurements.set(key, new Promise(function (resolve) {
          // Keep the displayed image working even if its host disallows canvas CORS.
          const probe = new Image();
          probe.crossOrigin = 'anonymous';
          probe.onload = function () {
            try {
              const scale = 256 / Math.max(probe.naturalWidth, probe.naturalHeight);
              const canvas = document.createElement('canvas');
              canvas.width = Math.max(1, Math.round(probe.naturalWidth * scale));
              canvas.height = Math.max(1, Math.round(probe.naturalHeight * scale));
              const context = canvas.getContext('2d', { willReadFrequently: true });
              context.drawImage(probe, 0, 0, canvas.width, canvas.height);
              resolve(getLogoPlacement(getLogoBounds(context.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height, ignoreWhite)));
            } catch (_) { resolve(null); }
          };
          probe.onerror = function () { resolve(null); };
          cleanups.push(function () { probe.onload = probe.onerror = null; resolve(null); });
          probe.src = source;
        }));
      }
      logoMeasurements.get(key).then(function (offset) {
        if ((img.currentSrc || img.src) !== source) return;
        if (abort.signal.aborted || !img.isConnected || (img.currentSrc || img.src) !== source) return;
        if (offset) {
          img.style.setProperty('--nfl-logo-offset-x', offset.x + '%');
          img.style.setProperty('--nfl-logo-offset-y', offset.y + '%');
          img.style.setProperty('--nfl-logo-scale', offset.scale);
          img.dataset.logoCentered = 'true';
        }
        markLogoReady(img);
      });
    }
    function prepareLogo(img) {
      if (img.dataset.logoEventsReady === 'true') {
        if (img.complete) centerLogo(img);
        return;
      }
      img.dataset.logoEventsReady = 'true';
      img.addEventListener('load', function () { centerLogo(img); }, { signal: abort.signal });
      function handleLogoError() {
        const champSource = safeUrl(img.dataset.champLogoUrl, true);
        const retries = Number(img.dataset.champLogoRetries || 0);
        if (img.dataset.logoSource === 'champ' && champSource && retries < 2) {
          img.dataset.champLogoRetries = String(retries + 1);
          const retryId = root.setTimeout(function () {
            if (abort.signal.aborted || !img.isConnected || img.dataset.logoSource !== 'champ') return;
            const retryUrl = new URL(champSource);
            retryUrl.searchParams.set('retry', String(retries + 1));
            img.src = retryUrl.href;
          }, retries ? 1000 : 250);
          cleanups.push(function () { root.clearTimeout(retryId); });
          return;
        }
        if (schoolLogoFallback && img.src !== schoolLogoFallback && !img.closest('[data-shop-all], [data-nfl-shop-all]')) {
          img.removeAttribute('srcset');
          img.dataset.logoSource = 'placeholder';
          delete img.dataset.champLogoUrl;
          img.src = schoolLogoFallback;
        } else markLogoReady(img);
      }
      img.addEventListener('error', handleLogoError, { signal: abort.signal });
      if (img.complete && img.naturalWidth) centerLogo(img);
    }
    applySchoolCatalog(root.ChampSchoolCatalog);

    const schoolCatalogUrl = safeUrl(hub.dataset.schoolCatalogUrl, true);
    if (schoolCatalogUrl) {
      const catalogAbort = new AbortController();
      const catalogTimeout = root.setTimeout(function () { catalogAbort.abort(); }, 8000);
      cleanups.push(function () { catalogAbort.abort(); root.clearTimeout(catalogTimeout); });
      root.fetch(schoolCatalogUrl, { signal: catalogAbort.signal, credentials: 'omit' })
        .then(function (response) { if (!response.ok) throw new Error('School catalog unavailable'); return response.json(); })
        .then(function (payload) { if (hub.isConnected && Array.isArray(payload.schools)) applySchoolCatalog(payload.schools); })
        .catch(function () { /* Keep the bundled Champ catalog during an outage. */ })
        .finally(function () { root.clearTimeout(catalogTimeout); });
    }

    function setupRail(rail, controls) {
      const buttons = controls.querySelectorAll('[data-scroll]');
      function update() {
        const overflows = rail.scrollWidth > rail.clientWidth + 2;
        controls.hidden = !overflows;
        if (rail === featuredRail) {
          rail.classList.toggle('is-centered', !overflows);
          const firstCard = rail.firstElementChild;
          const inset = !overflows && firstCard
            ? Math.max(0, firstCard.getBoundingClientRect().left - rail.getBoundingClientRect().left)
            : 0;
          hub.style.setProperty('--nfl-featured-inset', inset + 'px');
        }
        buttons[0].disabled = rail.scrollLeft <= 2;
        buttons[1].disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
        if (rail === teamRail) {
          rail.parentElement.classList.toggle('can-scroll-back', !controls.hidden && !buttons[0].disabled);
          rail.parentElement.classList.toggle('can-scroll-forward', !controls.hidden && !buttons[1].disabled);
        }
      }
      buttons.forEach(function (button) {
        button.addEventListener('click', function () {
          rail.scrollBy({ left: Number(button.dataset.scroll) * rail.clientWidth * 0.8, behavior: root.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }, { signal: abort.signal });
      });
      rail.addEventListener('scroll', update, { passive: true, signal: abort.signal });
      if (root.ResizeObserver) {
        const observer = new ResizeObserver(update);
        observer.observe(rail);
        cleanups.push(function () { observer.disconnect(); });
      }
      root.addEventListener('resize', update, { signal: abort.signal });
      update();
      return update;
    }
    const updateTeams = setupRail(teamRail, hub.querySelector('[data-team-controls]'));
    const updateFeatures = setupRail(featuredRail, hub.querySelector('[data-featured-controls]'));
    const updateTopProducts = topProductsRail
      ? setupRail(topProductsRail, hub.querySelector('[data-top-products-controls]'))
      : function () {};
    function formatMoney(price) {
      if (!price) return '';
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: price.currencyCode }).format(price.amount);
      } catch (_) {
        return '$' + Number(price.amount).toFixed(2);
      }
    }
    function renderTopProducts(payload) {
      if (!topProducts || !topProductsRail) return;
      const data = selectTopProducts(payload);
      if (!data || !data.tabs.some(function (tab) { return data.lists[tab.key].length; })) {
        topProducts.hidden = true;
        topProductsRail.replaceChildren();
        updateTopProducts();
        return;
      }
      const tabs = hub.querySelector('[data-top-products-tabs]');
      const empty = hub.querySelector('[data-top-products-empty]');
      hub.querySelector('[data-top-products-title]').textContent = data.heading;
      tabs.replaceChildren();
      function showList(key) {
        const products = data.lists[key] || [];
        topProductsRail.replaceChildren();
        products.forEach(function (product) {
          const li = node('li');
          const card = node('article', 'nfl-hub__top-product');
          const media = node('div', 'nfl-hub__top-product-media');
          const imageLink = node('a', 'nfl-hub__top-product-image-link');
          imageLink.href = product.url;
          const img = node('img');
          img.src = product.image;
          img.alt = product.alt;
          img.width = 600;
          img.height = 600;
          img.loading = 'lazy';
          imageLink.append(img);
          media.append(imageLink, node('span', 'nfl-hub__top-product-rank', '#' + product.rank));
          if (product.handle) {
            const wishlist = node('button', 'collection-wishlist-btn nfl-hub__top-product-wishlist');
            wishlist.type = 'button';
            wishlist.setAttribute('aria-label', 'Add ' + product.title + ' to wishlist');
            wishlist.dataset.productId = product.productId;
            wishlist.dataset.productHandle = product.handle;
            wishlist.dataset.productTitle = product.title;
            wishlist.dataset.productImage = product.image;
            wishlist.dataset.productPrice = product.price ? String(product.price.amount) : '';
            wishlist.innerHTML = '<svg class="heart-outline" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg><svg class="heart-filled" viewBox="0 0 24 24" aria-hidden="true" fill="#d1000a" stroke="#d1000a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>';
            if (root.WLCache && root.WLCache.isLoaded() && root.WLCache.isWishlisted(product.productId)) {
              wishlist.classList.add('in-wishlist');
            }
            media.append(wishlist);
          }
          const meta = node('div', 'nfl-hub__top-product-meta');
          meta.append(
            node('span', '', product.productType),
            node('span', '', formatMoney(product.price))
          );
          const copyLink = node('a', 'nfl-hub__top-product-copy');
          copyLink.href = product.url;
          copyLink.append(meta, node('h3', 'tt-title nfl-hub__top-product-title', product.title));
          card.append(media, copyLink);
          li.append(card);
          topProductsRail.append(li);
        });
        empty.hidden = products.length > 0;
        topProductsRail.hidden = products.length === 0;
        tabs.querySelectorAll('[role="tab"]').forEach(function (button) {
          const selected = button.dataset.topProductsTab === key;
          button.setAttribute('aria-selected', selected ? 'true' : 'false');
          button.tabIndex = selected ? 0 : -1;
        });
        topProductsRail.scrollLeft = 0;
        updateTopProducts();
      }
      data.tabs.forEach(function (tab, index) {
        const button = node('button', '', tab.label);
        button.type = 'button';
        button.role = 'tab';
        button.dataset.topProductsTab = tab.key;
        button.setAttribute('aria-controls', topProductsRail.id);
        button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        button.addEventListener('click', function () { showList(tab.key); }, { signal: abort.signal });
        tabs.append(button);
      });
      topProducts.hidden = false;
      showList(data.tabs[0].key);
    }
    const teamBrowser = hub.querySelector('.nfl-hub__team-browser');
    const teamWrap = teamRail.parentElement;
    const teamHeadingTitle = hub.querySelector(
      '.nfl-hub__teams-heading .nfl-hub__section-title'
    );
    function alignTeamRail() {
      if (teamHeadingTitle) {
        const hubBounds = hub.getBoundingClientRect();
        const hubStyles = root.getComputedStyle(hub);
        const contentLeft = hubBounds.left + (parseFloat(hubStyles.paddingLeft) || 0);
        const headingLeft = teamHeadingTitle.getBoundingClientRect().left;
        hub.style.setProperty(
          '--nfl-top-products-inset',
          Math.max(0, headingLeft - contentLeft) + 'px'
        );
      }
      if (!root.matchMedia('(min-width: 1025px)').matches) {
        teamWrap.style.removeProperty('margin-right');
      } else {
        const browserBounds = teamBrowser.getBoundingClientRect();
        const fourth = featuredRail.children[3];
        // Scroll snapping places the first billboard at the rail's left edge.
        const snapInset = featuredRail.scrollWidth > featuredRail.clientWidth ? parseFloat(root.getComputedStyle(featuredRail).paddingLeft) || 0 : 0;
        const right = fourth && !featured.hidden ? fourth.getBoundingClientRect().right + featuredRail.scrollLeft - snapInset : browserBounds.right;
        teamWrap.style.marginRight = Math.max(0, browserBounds.right - right) + 'px';
      }
      updateTeams();
    }
    if (root.ResizeObserver) {
      const observer = new ResizeObserver(alignTeamRail);
      observer.observe(teamBrowser);
      observer.observe(featuredRail);
      if (teamHeadingTitle) observer.observe(teamHeadingTitle);
      cleanups.push(function () { observer.disconnect(); });
    }
    root.addEventListener('resize', alignTeamRail, { signal: abort.signal });
    let dialogTrigger = toggle;
    function openTeamDialog(trigger) {
      if (dialog.open) return;
      dialogTrigger = trigger || toggle;
      dialog.showModal();
      teamScroll.scrollTop = 0;
      if (schoolSearch) {
        schoolSearch.value = '';
        updateSchoolSearch();
      }
    }
    toggle.addEventListener('click', function () { openTeamDialog(toggle); }, { signal: abort.signal });
    const shopAllLink = shopAllTile && shopAllTile.matches('[data-shop-all]')
      ? shopAllTile.querySelector('.nfl-hub__team')
      : null;
    if (shopAllLink) {
      shopAllLink.addEventListener('click', function (event) {
        event.preventDefault();
        openTeamDialog(shopAllLink);
      }, { signal: abort.signal });
    }
    const warmedSchoolLogos = new Set();
    const schoolLogoWarmupQueue = [];
    let schoolLogoWarmupActive = 0;
    let schoolLogoWarmupConcurrency = 3;
    function pumpSchoolLogoWarmup() {
      while (schoolLogoWarmupActive < schoolLogoWarmupConcurrency && schoolLogoWarmupQueue.length) {
        const source = schoolLogoWarmupQueue.shift();
        const preload = new Image();
        schoolLogoWarmupActive += 1;
        preload.crossOrigin = 'anonymous';
        preload.decoding = 'async';
        preload.fetchPriority = 'low';
        preload.onload = preload.onerror = function () {
          schoolLogoWarmupActive -= 1;
          pumpSchoolLogoWarmup();
        };
        preload.src = source;
      }
    }
    function queueSchoolLogos(limit) {
      const images = Array.from(teamGrid.querySelectorAll('img[data-logo-source="champ"]'));
      (limit ? images.slice(0, limit) : images).forEach(function (img) {
        const source = img.currentSrc || img.src;
        if (!source || warmedSchoolLogos.has(source)) return;
        warmedSchoolLogos.add(source);
        schoolLogoWarmupQueue.push(source);
      });
      pumpSchoolLogoWarmup();
    }
    function prioritizeSchoolLogos() {
      schoolLogoWarmupConcurrency = 6;
      queueSchoolLogos(42);
    }
    function startSchoolLogoWarmup() {
      const connection = root.navigator && root.navigator.connection;
      const constrained = connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || ''));
      // Warm only the first picker viewport. Preloading the entire directory
      // competes with the visible rail and can overwhelm the logo endpoint.
      const start = function () { queueSchoolLogos(constrained ? 12 : 42); };
      if (root.requestIdleCallback) {
        const idleId = root.requestIdleCallback(start, { timeout: 800 });
        cleanups.push(function () { root.cancelIdleCallback(idleId); });
      } else {
        const warmupId = root.setTimeout(start, 150);
        cleanups.push(function () { root.clearTimeout(warmupId); });
      }
    }
    if (schoolLogoFallback) {
      [toggle, shopAllLink].filter(Boolean).forEach(function (trigger) {
        trigger.addEventListener('pointerenter', prioritizeSchoolLogos, { once: true, signal: abort.signal });
        trigger.addEventListener('focus', prioritizeSchoolLogos, { once: true, signal: abort.signal });
        trigger.addEventListener('touchstart', prioritizeSchoolLogos, { once: true, passive: true, signal: abort.signal });
      });
      startSchoolLogoWarmup();
    }
    hub.querySelector('[data-team-close]').addEventListener('click', function () { dialog.close(); }, { signal: abort.signal });
    dialog.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        dialog.close();
      }
    }, { signal: abort.signal });
    dialog.addEventListener('click', function (event) {
      const bounds = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) dialog.close();
    }, { signal: abort.signal });
    dialog.addEventListener('close', function () {
      dialogTrigger.focus({ preventScroll: true });
    }, { signal: abort.signal });
    cleanups.push(function () { dialog.close(); });

    const endpoint = safeUrl(hub.dataset.featuredUrl, true);
    // NCAA reserves its featured-content height with the Shopify fallback.
    // Keep the hero concealed until its published image is ready to avoid a flash.
    featured.hidden = instantFallback
      ? !featuredRail.children.length
      : endpoint ? true : !featuredRail.children.length;
    if (!endpoint) showFallbackHero();
    alignTeamRail();
    if (endpoint) {
      // Keep request cancellation separate from UI listeners on a slow endpoint.
      const requestAbort = new AbortController();
      const requestTimeout = root.setTimeout(function () { requestAbort.abort(); }, 8000);
      cleanups.push(function () { requestAbort.abort(); root.clearTimeout(requestTimeout); });
      root.fetch(endpoint, { signal: requestAbort.signal, credentials: 'omit' }).then(function (response) {
        if (!response.ok) throw new Error('Featured content unavailable');
        return response.json();
      }).then(function (payload) {
        const items = selectFeatures(payload);
        if (!hub.isConnected) return;
        const publishedHero = selectHero(payload, league.toUpperCase() + ' fan shop');
        showPublishedHero(publishedHero);
        renderTopProducts(payload);
        if (items === null) {
          featured.hidden = !featuredRail.children.length;
          updateFeatures();
          alignTeamRail();
          return;
        }
        featuredRail.replaceChildren();
        hub.querySelector('[data-featured-title]').textContent = payload.heading || 'Trending collections';
        items.forEach(function (item) {
          const li = node('li');
          const link = node('a', 'nfl-hub__billboard');
          link.href = featureDestination(item, { collectionHandle: collectionHandle, league: league });
          const art = node('div', 'nfl-hub__art');
          const picture = node('picture');
          if (safeUrl(item.mobileImage, true)) {
            const source = node('source');
            source.media = '(max-width: 600px)';
            source.srcset = safeUrl(item.mobileImage, true);
            picture.append(source);
          }
          const img = node('img');
          img.src = safeUrl(item.image, true);
          img.alt = item.alt || item.title;
          img.width = 600;
          img.height = 800;
          img.loading = 'lazy';
          picture.append(img);
          art.append(picture);
          link.append(art, node('h3', 'tt-title nfl-hub__card-title', item.title));
          if (item.subtitle) link.append(node('p', 'nfl-hub__card-price', item.subtitle));
          li.append(link);
          featuredRail.append(li);
        });
        featured.hidden = items.length === 0;
        updateFeatures();
        alignTeamRail();
      }).catch(function () {
        if (!hub.isConnected) return;
        showFallbackHero();
        if (topProducts) topProducts.hidden = true;
        featured.hidden = !featuredRail.children.length;
        updateFeatures();
        alignTeamRail();
      }).finally(function () { root.clearTimeout(requestTimeout); });
    }
    hub.leagueHubCleanup = function () { abort.abort(); cleanups.forEach(function (cleanup) { cleanup(); }); };
    hub.nflCleanup = hub.leagueHubCleanup;
  }
  function start(scope) { scope.querySelectorAll('[data-league-hub], [data-nfl-hub]').forEach(init); }
  if (!root.NflHubRegistered) {
    root.NflHubRegistered = true;
    document.addEventListener('shopify:section:load', function (event) { start(event.target); });
    document.addEventListener('shopify:section:unload', function (event) {
      event.target.querySelectorAll('[data-league-hub], [data-nfl-hub]').forEach(function (hub) { if (hub.leagueHubCleanup || hub.nflCleanup) (hub.leagueHubCleanup || hub.nflCleanup)(); });
    });
  }
  start(document);
})(typeof window === 'undefined' ? {} : window);
